import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Loader2, AlertCircle, Check, ArrowLeft } from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';

declare global {
  interface Window {
    paypal?: {
      CardFields: (options: PayPalCardFieldsOptions) => PayPalCardFieldsInstance;
    };
  }
}

interface PayPalCardFieldsOptions {
  createOrder: () => Promise<string>;
  onApprove: (data: { orderID: string }) => Promise<void>;
  onError: (error: Error) => void;
  style?: {
    input?: Record<string, string>;
    '.invalid'?: Record<string, string>;
  };
}

interface PayPalCardFieldsInstance {
  isEligible: () => boolean;
  NameField: () => { render: (selector: string) => Promise<void> };
  NumberField: () => { render: (selector: string) => Promise<void> };
  ExpiryField: () => { render: (selector: string) => Promise<void> };
  CVVField: () => { render: (selector: string) => Promise<void> };
  submit: () => Promise<void>;
  getState: () => {
    fields: {
      cardNameField: { isEmpty: boolean; isValid: boolean };
      cardNumberField: { isEmpty: boolean; isValid: boolean };
      cardExpiryField: { isEmpty: boolean; isValid: boolean };
      cardCvvField: { isEmpty: boolean; isValid: boolean };
    };
    isFormValid: boolean;
  };
}

interface User {
  id: number;
  email: string;
  name: string;
  organization_id: number;
  role: string;
  subscription_tier: string;
  credits_remaining: number;
}

interface PayPalCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onSuccess: (data: { type: string; tier?: string; amount?: number; user?: User }) => void;
  purchaseType: 'subscription' | 'credits';
  plan?: string;
  interval?: 'monthly' | 'yearly';
  creditAmount?: number;
  title?: string;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

export default function PayPalCardModal({
  isOpen,
  onClose,
  onBack,
  onSuccess,
  purchaseType,
  plan,
  interval,
  creditAmount,
  title,
}: PayPalCardModalProps) {
  const { theme } = useThemeStore();
  const { token } = useAuthStore();
  const isDark = theme === 'dark';

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardFieldsReady, setCardFieldsReady] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  const cardFieldsRef = useRef<PayPalCardFieldsInstance | null>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  // Load PayPal SDK
  const loadPayPalSDK = useCallback(async () => {
    try {
      // Fetch client token and client ID from backend
      const response = await fetch(`${API_URL}/billing/paypal/client-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json() as {
        success: boolean;
        data?: { clientToken: string; clientId: string; isLive: boolean };
        error?: string;
      };

      if (!data.success || !data.data) {
        throw new Error(data.error || 'Failed to get PayPal client token');
      }

      const { clientId, clientToken, isLive } = data.data;

      // Check if SDK already loaded
      if (window.paypal?.CardFields) {
        setSdkLoaded(true);
        setIsLoading(false);
        return;
      }

      // Remove existing script if any
      const existingScript = document.querySelector('script[data-paypal-sdk]');
      if (existingScript) {
        existingScript.remove();
      }

      // Load PayPal SDK with card fields component
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&components=card-fields&currency=USD${isLive ? '' : '&buyer-country=US'}`;
      script.setAttribute('data-paypal-sdk', 'true');
      script.setAttribute('data-client-token', clientToken);

      script.onload = () => {
        setSdkLoaded(true);
        setIsLoading(false);
      };

      script.onerror = () => {
        setError('Failed to load payment SDK');
        setIsLoading(false);
      };

      document.body.appendChild(script);
      scriptRef.current = script;
    } catch (err) {
      console.error('PayPal SDK load error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize payment');
      setIsLoading(false);
    }
  }, [token]);

  // Initialize card fields
  const initializeCardFields = useCallback(async () => {
    if (!window.paypal?.CardFields || cardFieldsRef.current) return;

    try {
      const cardFields = window.paypal.CardFields({
        createOrder: async () => {
          const response = await fetch(`${API_URL}/billing/paypal/card/create-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              type: purchaseType,
              plan,
              interval,
              amount: creditAmount,
            }),
          });

          const data = await response.json() as {
            success: boolean;
            data?: { orderId: string };
            error?: string;
          };

          if (!data.success || !data.data?.orderId) {
            throw new Error(data.error || 'Failed to create order');
          }

          return data.data.orderId;
        },
        onApprove: async (data) => {
          setIsProcessing(true);
          try {
            const response = await fetch(`${API_URL}/billing/paypal/card/capture`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ order_id: data.orderID }),
            });

            const result = await response.json() as {
              success: boolean;
              data?: { type: string; tier?: string; amount?: number; user?: User };
              error?: string;
            };

            if (result.success && result.data) {
              onSuccess(result.data);
              onClose();
            } else {
              throw new Error(result.error || 'Payment failed');
            }
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Payment failed');
            setIsProcessing(false);
          }
        },
        onError: (err) => {
          console.error('PayPal card error:', err);
          setError(err.message || 'Payment failed');
          setIsProcessing(false);
        },
        style: {
          input: {
            'font-size': '16px',
            'font-family': 'Inter, system-ui, sans-serif',
            'font-weight': '400',
            color: isDark ? '#ffffff' : '#1e293b',
            padding: '12px 16px',
          },
          '.invalid': {
            color: '#ef4444',
          },
        },
      });

      if (cardFields.isEligible()) {
        cardFieldsRef.current = cardFields;

        // Render card fields
        await Promise.all([
          cardFields.NameField().render('#card-name-field'),
          cardFields.NumberField().render('#card-number-field'),
          cardFields.ExpiryField().render('#card-expiry-field'),
          cardFields.CVVField().render('#card-cvv-field'),
        ]);

        setCardFieldsReady(true);
      } else {
        setError('Card payments are not available. Please use PayPal instead.');
      }
    } catch (err) {
      console.error('Card fields init error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize card form');
    }
  }, [token, purchaseType, plan, interval, creditAmount, isDark, onSuccess, onClose]);

  // Load SDK when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setIsLoading(true);
      setCardFieldsReady(false);
      cardFieldsRef.current = null;
      loadPayPalSDK();
    }

    return () => {
      cardFieldsRef.current = null;
    };
  }, [isOpen, loadPayPalSDK]);

  // Initialize card fields when SDK is loaded
  useEffect(() => {
    if (sdkLoaded && isOpen && !cardFieldsRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeCardFields();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [sdkLoaded, isOpen, initializeCardFields]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cardFieldsRef.current) {
      setError('Card form not ready');
      return;
    }

    setError(null);
    setIsProcessing(true);

    try {
      await cardFieldsRef.current.submit();
    } catch (err) {
      console.error('Card submit error:', err);
      setError(err instanceof Error ? err.message : 'Payment failed');
      setIsProcessing(false);
    }
  };

  // Get display info
  const getDisplayInfo = () => {
    if (purchaseType === 'subscription' && plan) {
      const planNames: Record<string, string> = {
        pro: 'Pro',
        professional: 'Professional',
        enterprise: 'Enterprise',
      };
      const planPrices: Record<string, { monthly: number; yearly: number }> = {
        pro: { monthly: 25, yearly: 250 },
        professional: { monthly: 50, yearly: 500 },
        enterprise: { monthly: 150, yearly: 1500 },
      };
      const price = interval === 'yearly' ? planPrices[plan]?.yearly : planPrices[plan]?.monthly;
      return {
        title: title || `Subscribe to ${planNames[plan]} Plan`,
        amount: price || 0,
        description: `${planNames[plan]} Plan (${interval})`,
      };
    }

    if (purchaseType === 'credits' && creditAmount) {
      const creditPrices: Record<number, number> = {
        100: 1.99,
        600: 9.99,
        2400: 39.99,
        4200: 69.99,
      };
      return {
        title: title || `Buy ${creditAmount.toLocaleString()} Credits`,
        amount: creditPrices[creditAmount] || 0,
        description: `${creditAmount.toLocaleString()} AI Credits`,
      };
    }

    return { title: title || 'Complete Payment', amount: 0, description: '' };
  };

  const displayInfo = getDisplayInfo();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className={`relative w-full max-w-md rounded-2xl p-6 ${
                isDark ? 'bg-dark-800 border border-dark-600' : 'bg-white shadow-xl'
              }`}
            >
              {/* Back Button */}
              <button
                onClick={onBack}
                disabled={isProcessing}
                className={`absolute top-4 left-4 p-2 rounded-lg transition-colors ${
                  isDark
                    ? 'hover:bg-dark-700 text-text-muted hover:text-white'
                    : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                } disabled:opacity-50`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              {/* Close Button */}
              <button
                onClick={onClose}
                disabled={isProcessing}
                className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                  isDark
                    ? 'hover:bg-dark-700 text-text-muted hover:text-white'
                    : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                } disabled:opacity-50`}
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-6 pt-2">
                <div
                  className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-4 ${
                    isDark ? 'bg-accent-purple/20' : 'bg-purple-100'
                  }`}
                >
                  <CreditCard className="w-7 h-7 text-accent-purple" />
                </div>
                <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {displayInfo.title}
                </h2>
                {displayInfo.amount > 0 && (
                  <p className="text-2xl font-bold text-accent-purple">
                    ${displayInfo.amount.toFixed(2)}
                  </p>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}

              {/* Loading State */}
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-accent-purple animate-spin mb-4" />
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    Loading payment form...
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Card Name Field */}
                  <div className="mb-4">
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-text-secondary' : 'text-slate-700'
                      }`}
                    >
                      Cardholder Name
                    </label>
                    <div
                      id="card-name-field"
                      className={`h-12 rounded-xl border transition-colors ${
                        isDark
                          ? 'bg-dark-700 border-dark-600'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    />
                  </div>

                  {/* Card Number Field */}
                  <div className="mb-4">
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-text-secondary' : 'text-slate-700'
                      }`}
                    >
                      Card Number
                    </label>
                    <div
                      id="card-number-field"
                      className={`h-12 rounded-xl border transition-colors ${
                        isDark
                          ? 'bg-dark-700 border-dark-600'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    />
                  </div>

                  {/* Expiry and CVV */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-text-secondary' : 'text-slate-700'
                        }`}
                      >
                        Expiry Date
                      </label>
                      <div
                        id="card-expiry-field"
                        className={`h-12 rounded-xl border transition-colors ${
                          isDark
                            ? 'bg-dark-700 border-dark-600'
                            : 'bg-slate-50 border-slate-200'
                        }`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-text-secondary' : 'text-slate-700'
                        }`}
                      >
                        CVV
                      </label>
                      <div
                        id="card-cvv-field"
                        className={`h-12 rounded-xl border transition-colors ${
                          isDark
                            ? 'bg-dark-700 border-dark-600'
                            : 'bg-slate-50 border-slate-200'
                        }`}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={!cardFieldsReady || isProcessing}
                    className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        <span>Pay ${displayInfo.amount.toFixed(2)}</span>
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Security Badge */}
              <div className="mt-4 flex items-center justify-center gap-2">
                <svg
                  className={`w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                  Secured by PayPal encryption
                </span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
